/**
 * FileMakerClipboard.ts - FileMaker XML clipboard integration for Clippr
 * 
 * CHANGE HISTORY:
 * - 251102_1138 [pending] danielbeer: Initial creation for FileMaker XML clipboard support
 * 
 * Converts FileMaker XML (fmxmlsnippet) to/from system clipboard as FileMaker objects.
 * Uses FmClipTools pattern for clipboard manipulation.
 */

export interface FileMakerClipboardItem {
  xml: string;
  type: 'Script' | 'CustomFunction' | 'Layout' | 'BaseTable' | 'Field' | 'Unknown';
  name?: string;
  id?: string;
  metadata?: Record<string, any>;
}

export interface ClipboardEntry {
  types: string[];
  data: Map<string, any>;
  timestamp: number;
  source?: string;
}

/**
 * FileMaker Clipboard Manager
 * Handles conversion between FileMaker XML and system clipboard
 */
export class FileMakerClipboard {
  
  /**
   * Write FileMaker XML to system clipboard as FileMaker object
   * This mimics what FileMaker does when you copy a script/function
   */
  async writeFileMakerObject(xml: string): Promise<boolean> {
    try {
      // Parse XML to validate and extract metadata
      const item = this.parseFileMakerXML(xml);
      
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        // Create clipboard item with multiple formats
        const clipboardData = new ClipboardItem({
          // Plain text format (for text editors)
          'text/plain': new Blob([xml], { type: 'text/plain' }),
          
          // HTML format (for rich text editors)
          'text/html': new Blob([this.xmlToHTML(xml, item)], { type: 'text/html' }),
          
          // FileMaker-specific format (if supported by browser)
          // Note: Custom MIME types may not work in all browsers
          'application/x-filemaker-xml': new Blob([xml], { type: 'application/x-filemaker-xml' })
        });
        
        await navigator.clipboard.write([clipboardData]);
        return true;
      }
      
      // Fallback: Just write as text
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(xml);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to write FileMaker object to clipboard:', error);
      return false;
    }
  }
  
  /**
   * Read FileMaker XML from system clipboard
   */
  async readFileMakerObject(): Promise<FileMakerClipboardItem | null> {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        // Try to read as text first
        const text = await navigator.clipboard.readText();
        
        // Check if it's FileMaker XML
        if (text.includes('fmxmlsnippet')) {
          return this.parseFileMakerXML(text);
        }
        
        // Try to read clipboard items with multiple types
        const items = await navigator.clipboard.read();
        for (const item of items) {
          // Check for FileMaker-specific format
          if (item.types.includes('application/x-filemaker-xml')) {
            const blob = await item.getType('application/x-filemaker-xml');
            const xml = await blob.text();
            return this.parseFileMakerXML(xml);
          }
          
          // Check plain text
          if (item.types.includes('text/plain')) {
            const blob = await item.getType('text/plain');
            const text = await blob.text();
            if (text.includes('fmxmlsnippet')) {
              return this.parseFileMakerXML(text);
            }
          }
        }
      }
      
      return null;
    } catch (error) {
      console.error('Failed to read FileMaker object from clipboard:', error);
      return null;
    }
  }
  
  /**
   * Parse FileMaker XML and extract metadata
   */
  parseFileMakerXML(xml: string): FileMakerClipboardItem {
    const item: FileMakerClipboardItem = {
      xml,
      type: 'Unknown'
    };
    
    try {
      // Extract type
      if (xml.includes('<Script')) {
        item.type = 'Script';
        const nameMatch = xml.match(/name="([^"]+)"/);
        const idMatch = xml.match(/id="([^"]+)"/);
        if (nameMatch) item.name = nameMatch[1];
        if (idMatch) item.id = idMatch[1];
      } else if (xml.includes('<CustomFunction')) {
        item.type = 'CustomFunction';
        const nameMatch = xml.match(/name="([^"]+)"/);
        const idMatch = xml.match(/id="([^"]+)"/);
        if (nameMatch) item.name = nameMatch[1];
        if (idMatch) item.id = idMatch[1];
      } else if (xml.includes('<Layout')) {
        item.type = 'Layout';
        const nameMatch = xml.match(/name="([^"]+)"/);
        if (nameMatch) item.name = nameMatch[1];
      } else if (xml.includes('<BaseTable')) {
        item.type = 'BaseTable';
        const nameMatch = xml.match(/name="([^"]+)"/);
        if (nameMatch) item.name = nameMatch[1];
      } else if (xml.includes('<Field')) {
        item.type = 'Field';
        const nameMatch = xml.match(/name="([^"]+)"/);
        if (nameMatch) item.name = nameMatch[1];
      }
      
      // Extract additional metadata
      item.metadata = {
        hasSteps: xml.includes('<Step'),
        stepCount: (xml.match(/<Step/g) || []).length,
        hasCalculation: xml.includes('<Calculation>'),
        size: xml.length
      };
      
    } catch (error) {
      console.error('Error parsing FileMaker XML:', error);
    }
    
    return item;
  }
  
  /**
   * Convert FileMaker XML to HTML for rich text display
   */
  private xmlToHTML(xml: string, item: FileMakerClipboardItem): string {
    const escapedXML = xml
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    return `
      <div style="font-family: monospace; padding: 10px; background: #f5f5f5; border: 1px solid #ddd;">
        <div style="font-weight: bold; margin-bottom: 10px;">
          FileMaker ${item.type}: ${item.name || 'Unnamed'}
        </div>
        <pre style="white-space: pre-wrap; word-wrap: break-word;">${escapedXML}</pre>
      </div>
    `;
  }
  
  /**
   * Inspect system clipboard and return all available types
   */
  async inspectClipboard(): Promise<ClipboardEntry[]> {
    const entries: ClipboardEntry[] = [];
    
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        const items = await navigator.clipboard.read();
        
        for (const item of items) {
          const entry: ClipboardEntry = {
            types: [...item.types],
            data: new Map(),
            timestamp: Date.now()
          };
          
          // Read each type
          for (const type of item.types) {
            try {
              const blob = await item.getType(type);
              
              // Convert to appropriate format
              if (type.startsWith('text/')) {
                entry.data.set(type, await blob.text());
              } else if (type.startsWith('image/')) {
                entry.data.set(type, {
                  blob,
                  size: blob.size,
                  type: blob.type,
                  url: URL.createObjectURL(blob)
                });
              } else {
                entry.data.set(type, {
                  blob,
                  size: blob.size,
                  type: blob.type
                });
              }
            } catch (error) {
              console.warn(`Failed to read clipboard type ${type}:`, error);
              entry.data.set(type, { error: (error as Error).message });
            }
          }
          
          entries.push(entry);
        }
      }
    } catch (error) {
      console.error('Failed to inspect clipboard:', error);
    }
    
    return entries;
  }
  
  /**
   * Get clipboard type counts
   */
  async getClipboardTypeCounts(): Promise<Record<string, number>> {
    const entries = await this.inspectClipboard();
    const counts: Record<string, number> = {};
    
    for (const entry of entries) {
      for (const type of entry.types) {
        counts[type] = (counts[type] || 0) + 1;
      }
    }
    
    return counts;
  }
  
  /**
   * Check if clipboard contains FileMaker XML
   */
  async hasFileMakerObject(): Promise<boolean> {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        const text = await navigator.clipboard.readText();
        return text.includes('fmxmlsnippet');
      }
    } catch (error) {
      console.warn('Failed to check for FileMaker object:', error);
    }
    return false;
  }
  
  /**
   * Format clipboard entry for display
   */
  formatClipboardEntry(entry: ClipboardEntry): string {
    const lines: string[] = [];
    
    lines.push(`Clipboard Entry (${entry.types.length} types):`);
    lines.push(`Timestamp: ${new Date(entry.timestamp).toLocaleString()}`);
    lines.push('');
    
    for (const [type, data] of entry.data.entries()) {
      lines.push(`Type: ${type}`);
      
      if (typeof data === 'string') {
        const preview = data.length > 100 ? data.substring(0, 100) + '...' : data;
        lines.push(`  Content: ${preview}`);
        lines.push(`  Size: ${data.length} characters`);
      } else if (data.blob) {
        lines.push(`  Size: ${data.size} bytes`);
        lines.push(`  MIME: ${data.type}`);
        if (data.url) {
          lines.push(`  URL: ${data.url}`);
        }
      } else if (data.error) {
        lines.push(`  Error: ${data.error}`);
      }
      
      lines.push('');
    }
    
    return lines.join('\n');
  }
}

// Singleton instance
let instance: FileMakerClipboard | null = null;

export const getFileMakerClipboard = (): FileMakerClipboard => {
  if (!instance) {
    instance = new FileMakerClipboard();
  }
  return instance;
};

export default FileMakerClipboard;
